import { Box, Divider, Link } from '@mui/material';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

import { ICompanyTargetGroupDataForReview } from '@/entities/company-target-group/domain/company-target-group.types';

import { useAppSelector } from '../../../../../app/store/utils/redux.hooks';
import { companyForReviewSelector } from '../../../../../entities/company-target-group/store/company-target-group.selectors';
import {
  CloseButton,
  CompanyAnalysisSource,
  CompanyDescription,
  CompanyDescriptionContainer,
  CompanyDescriptionTitle,
  CompanyDetailsPanelContainer,
  CompanyHeader,
  CompanyName,
  CompanySubtitle,
  DetailsBox,
  LoadingOverlay,
  SocialLinks,
} from './company-details-panel.styles';
import CompanyDetailsBox from './components/company-details-box/company-details-box.component';
import CustomFieldsSection from './components/custom-fields-section/custom-fields-section.component';
import AcquiredCompaniesSection from './components/details-section/acquired-companies-section.component';
import DetailsSection from './components/details-section/details-section.component';
import FinancialsSection from './components/details-section/financials-section.component';
import LastFundraiseSection from './components/details-section/last-fundraise-section.component';
import LeadershipSection from './components/details-section/leadership-section.component';
import NewsEventsSection from './components/details-section/news-events-section.component';
import { hasValidFinancialData } from './utils/financial-utils';

function insertNewlineAfterLinks(markdown: string): string {
  // Regex to match one or more consecutive markdown links
  // and insert a newline before the first letter/number after them
  return markdown.replace(
    /((?:\[[^\]]*\]\([^)]+\)\s*)+)([\s.,;:]*)([a-zA-Z0-9])/g,
    (_match, links, _punct, nextChar) => {
      return `${links}\n${nextChar}`;
    },
  );
}

function checkURL(url: string): boolean {
  // url is in this format: [title](https://www.google.com)
  // we need to extract the url from the string
  const urlRegex = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/;
  const match = url.match(urlRegex);
  if (match) {
    try {
      new URL(match[2]);
      return true;
    } catch (error) {
      return false;
    }
  }
  return false;
}

function InvestorDetailsSection({ title, investors }: { title: string; investors?: any[] }) {
  if (!investors || investors.length === 0) return null;
  return (
    <DetailsSection title={title} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {investors.map((inv, idx) => (
          <Box
            key={inv.name + inv.date + idx}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              gap: 2,
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
            }}
          >
            <span style={{ minWidth: 160, fontWeight: 500 }}>{inv.name}</span>
            <span style={{ minWidth: 120, color: '#888', textAlign: 'center' }}>{inv.date}</span>
            {inv.source && checkURL(inv.source) ? (
              <ReactMarkdown
                className="!flex !items-center !justify-center !w-full !min-w-0 !overflow-hidden !rounded-md"
                components={{
                  p: ({ children }) => <>{children}</>,
                  a: ({ node, ref, href, children, ...props }) => (
                    <CompanyAnalysisSource
                      {...props}
                      href={href}
                      target="_blank"
                      className="!inline-flex !items-center !gap-2 !rounded-full !bg-transparent !text-gray-900 !font-medium !transition-all hover:!bg-gray-200 hover:!shadow-md hover:!scale-[1.03] !w-full !max-w-[90%] !my-1"
                    >
                      {href && (
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${new URL(href).hostname}`}
                          alt="Favicon"
                          className="!flex-shrink-0 !w-5 !h-5 !bg-white !border !border-gray-200 !rounded-full !shadow-sm"
                        />
                      )}
                      <span className="!w-full !min-w-0 !not-italic !font-medium !normal-case !truncate">
                        {children}
                      </span>
                    </CompanyAnalysisSource>
                  ),
                }}
              >
                {inv.source}
              </ReactMarkdown>
            ) : (
              <div></div>
            )}
          </Box>
        ))}
      </Box>
    </DetailsSection>
  );
}

export function CompanyDetailsPanel({
  isCompanySelected,
  isClosing,
  handleClose,
  isLoading,
}: {
  isCompanySelected: boolean;
  isClosing: boolean;
  handleClose: () => void;
  isLoading: boolean;
}) {
  const companyForReview = useAppSelector(companyForReviewSelector);
  const { item } = companyForReview;

  const {
    companyName,
    legalName,
    headcount,
    companyNewsEvents,
    foundedYear,
    sixMonthHeadcountGrowth,
    companyLinkedIn,
    website,
    lastFundraise,
    acquisitions,
    revenue,
    profit,
    currency,
    revenueYear,
    profitYear,
    revenueEst,
    location,
    leads,
    businessType,
    parentCompany,
    ownership,
    analysis,
    customFields,
    vcInvestors,
    peInvestors,
    strategicInvestors,
  } = item as unknown as ICompanyTargetGroupDataForReview;

  const companyDetails = {
    description: businessType,
    location,
    foundedYear,
    headcount,
    sixMonthHeadcountGrowth,
    parentCompany,
    ownership,
    vcInvestors,
    peInvestors,
    strategicInvestors,
  };

  const hasFinancialData = hasValidFinancialData({
    revenue,
    profit,
    revenueEst,
  });

  const hasAcquisitionData = !!(acquisitions && acquisitions.length > 0);

  const availableSections: string[] = [];
  if (hasFinancialData) availableSections.push('financials');
  if (hasAcquisitionData) availableSections.push('acquisitions');
  if (lastFundraise) availableSections.push('lastFundraise');

  const availableSectionsLength = availableSections.length;

  const parsedAnalysis = useMemo(
    () => (analysis ? insertNewlineAfterLinks(analysis.body) : null),
    [analysis],
  );

  return (
    isCompanySelected && (
      <CompanyDetailsPanelContainer className={isClosing ? 'closing' : ''} loading={isLoading}>
        {/* TODO: improve animation */}
        <LoadingOverlay isLoading={isLoading}>
          <Box>Loading...</Box>
        </LoadingOverlay>

        <CloseButton onClick={handleClose} />
        <CompanyHeader>
          <div>
            <CompanyName variant="h3">{companyName}</CompanyName>
            <CompanySubtitle>{legalName}</CompanySubtitle>
          </div>

          <SocialLinks>
            {companyLinkedIn && (
              <Link href={companyLinkedIn} target="_blank">
                <img src="/icons/brand/linkedin.svg" alt="LinkedIn" width={16} height={16} />
              </Link>
            )}
            {website && (
              <Link href={website} target="_blank">
                <img src="/icons/website.svg" alt="Website" width={16} height={16} />
              </Link>
            )}
          </SocialLinks>
        </CompanyHeader>
        <DetailsBox
          sx={theme => ({
            gridTemplateColumns: '1fr',
            flexDirection: 'column',
            gap: '1rem',
            marginTop: '-1rem',

            [theme.breakpoints.down('lg')]: {
              display: 'flex',
            },
          })}
        >
          <Divider sx={{ borderColor: 'neutrals.gray.low' }} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              marginBottom: '0.5rem',
            }}
          >
            {analysis && (
              <CompanyDescriptionContainer>
                <CompanyDescriptionTitle showBorder={false}>Analysis</CompanyDescriptionTitle>
                <CompanyDescription color="neutrals.gray.higher">
                  {parsedAnalysis && (
                    <ReactMarkdown
                      remarkPlugins={[remarkBreaks]}
                      components={{
                        a: ({ node, ref, ...props }) => (
                          <CompanyAnalysisSource {...props} target="_blank" />
                        ),
                      }}
                    >
                      {parsedAnalysis}
                    </ReactMarkdown>
                  )}
                </CompanyDescription>
              </CompanyDescriptionContainer>
            )}
          </Box>

          <CompanyDetailsBox {...companyDetails} />

          <InvestorDetailsSection title="VC Investors" investors={vcInvestors} />
          <InvestorDetailsSection title="PE Investors" investors={peInvestors} />
          <InvestorDetailsSection title="Strategic Investors" investors={strategicInvestors} />
          <InvestorDetailsSection title="Parent Company" investors={parentCompany} />
        </DetailsBox>

        {availableSectionsLength > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${availableSectionsLength}, 1fr)`,
              gap: '24px',
              marginBottom: '24px',
            }}
          >
            {hasFinancialData && (
              <FinancialsSection
                revenue={revenue}
                profit={profit}
                revenueEst={revenueEst}
                profitYear={profitYear}
                revenueYear={revenueYear}
                currency={currency}
              />
            )}

            {hasAcquisitionData && <AcquiredCompaniesSection acquisitions={acquisitions} />}

            {lastFundraise && <LastFundraiseSection lastFundraise={lastFundraise} />}
          </Box>
        )}

        <LeadershipSection leads={leads} />

        {customFields && Object.keys(customFields).length > 0 && (
          <CustomFieldsSection customFields={customFields} />
        )}

        <NewsEventsSection newsEvents={companyNewsEvents?.companyNewsEvents?.data} />
      </CompanyDetailsPanelContainer>
    )
  );
}
